import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SearchIconSvg from '../svg/Search.svg';
import FilterIconSvg from '../svg/filter.svg';
import BellIconSvg from '../svg/bell.svg';
import ChevronDownIconSvg from '../svg/chevron-down.svg';
import LogoutIcon from '../svg/logout.svg';
import Modal from './requests/Modal';
import { 
  Bell, 
  X, 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  Truck, 
  User, 
  DollarSign, 
  TrendingUp, 
  Wrench, 
  Shield, 
  Clock, 
  MapPin,
  Activity,
  Zap,
  Car,
  Users,
  FileText,
  Calendar,
  CreditCard,
  AlertOctagon,
  ChevronDown,
  LogOut,
  Power
} from 'lucide-react';
import { apiService } from '../services';

// --- SVG Icon Components ---
// Using local SVG files for better performance and consistency

const SearchIcon = () => (
  <img src={SearchIconSvg} alt="Search" className="w-5 h-5 text-gray-500" />
);

const FilterIcon = () => (
  <img src={FilterIconSvg} alt="Filter" className="w-5 h-5 text-blue-950" />
);

const TimePeriodIcon = ({ active }) => (
    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? 'border-blue-950' : 'border-gray-400'}`}>
        {active && <div className="w-2 h-2 bg-blue-950 rounded-full"></div>}
    </div>
);

// --- Notification Component ---
export const NotificationDropdown = ({ userRole = 'fleet' }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Notification type configurations
  const notificationTypes = {
    // Fleet Vehicle & Driver Management
    FLEET_VEHICLE_REGISTERED: { icon: Truck, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Vehicle Registered' },
    FLEET_DRIVER_ASSIGNED_TO_VEHICLE: { icon: User, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'Driver Assigned' },
    FLEET_DRIVER_REGISTERED: { icon: User, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Driver Registered' },
    FLEET_DRIVER_LOGIN_CREDENTIALS: { icon: Shield, color: 'text-purple-600', bgColor: 'bg-purple-50', displayName: 'Login Credentials' },
    
    // Fleet Maintenance & Status
    FLEET_AMBULANCE_MAINTENANCE: { icon: Wrench, color: 'text-orange-600', bgColor: 'bg-orange-50', displayName: 'Maintenance Required' },
    FLEET_AMBULANCE_STATUS_CHANGED: { icon: Activity, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'Status Changed' },
    MAINTENANCE_REMINDER: { icon: Calendar, color: 'text-yellow-600', bgColor: 'bg-yellow-50', displayName: 'Maintenance Reminder' },
    
    // Fleet Booking & Operations
    FLEET_BOOKING_REQUEST: { icon: Bell, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Booking Request' },
    FLEET_DRIVER_REJECTED_BOOKING: { icon: X, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Driver Rejected' },
    
    // Fleet Performance & Analytics
    FLEET_PERFORMANCE_ALERT: { icon: TrendingUp, color: 'text-orange-600', bgColor: 'bg-orange-50', displayName: 'Performance Alert' },
    FLEET_DRIVER_AVAILABILITY_ALERT: { icon: Users, color: 'text-yellow-600', bgColor: 'bg-yellow-50', displayName: 'Driver Availability' },
    FLEET_VEHICLE_UTILIZATION_ALERT: { icon: Car, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'Vehicle Utilization' },
    
    // Fleet Safety & Security
    FLEET_PANIC_BUTTON_ALERT: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Panic Button Alert' },
    FLEET_VEHICLE_THEFT_ALERT: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Vehicle Theft Alert' },
    FLEET_DRIVER_SAFETY_INCIDENT: { icon: Shield, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Safety Incident' },
    FLEET_SECURITY_BREACH_ALERT: { icon: AlertOctagon, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Security Breach' },
    
    // Fleet Fuel & Operations
    FLEET_LOW_FUEL_ALERT: { icon: Zap, color: 'text-orange-600', bgColor: 'bg-orange-50', displayName: 'Low Fuel Alert' },
    FLEET_SPEED_LIMIT_VIOLATION: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Speed Violation' },
    FLEET_HARSH_DRIVING_ALERT: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Harsh Driving' },
    
    // Fleet Customer Service
    FLEET_CUSTOMER_COMPLAINT_ALERT: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Customer Complaint' },
    FLEET_CUSTOMER_FEEDBACK: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'Customer Feedback' },
    FLEET_SERVICE_DELAY_NOTIFICATION: { icon: Clock, color: 'text-orange-600', bgColor: 'bg-orange-50', displayName: 'Service Delay' },
    
    // Fleet Weather & Environmental
    FLEET_WEATHER_WARNING: { icon: AlertTriangle, color: 'text-yellow-600', bgColor: 'bg-yellow-50', displayName: 'Weather Warning' },
    FLEET_ROAD_CONDITION_ALERT: { icon: MapPin, color: 'text-orange-600', bgColor: 'bg-orange-50', displayName: 'Road Condition' },
    
    // Fleet Technology & System
    FLEET_APP_UPDATE_NOTIFICATION: { icon: Info, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'App Update' },
    FLEET_DATA_SYNC_ISSUE: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Sync Issue' },
    FLEET_SYSTEM_PERFORMANCE_ALERT: { icon: TrendingUp, color: 'text-orange-600', bgColor: 'bg-orange-50', displayName: 'System Performance' },
    
    // Fleet Regulatory & Compliance
    FLEET_INSPECTION_DUE_ALERT: { icon: Calendar, color: 'text-yellow-600', bgColor: 'bg-yellow-50', displayName: 'Inspection Due' },
    FLEET_PERMIT_RENEWAL_REMINDER: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'Permit Renewal' },
    FLEET_COMPLIANCE_VIOLATION_WARNING: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Compliance Violation' },
    
    // Fleet Equipment & Inventory
    FLEET_MEDICAL_EQUIPMENT_ALERT: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Equipment Alert' },
    FLEET_EQUIPMENT_MALFUNCTION: { icon: Wrench, color: 'text-orange-600', bgColor: 'bg-orange-50', displayName: 'Equipment Malfunction' },
    
    // Hospital notifications
    HOSPITAL_BOOKING_REQUEST: { icon: Bell, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Booking Request' },
    HOSPITAL_AMBULANCE_ASSIGNED: { icon: Truck, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Ambulance Assigned' },
    HOSPITAL_EMERGENCY_ALERT: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Emergency Alert' },
    
    // Super Admin notifications
    SYSTEM_ANNOUNCEMENT: { icon: Info, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'System Announcement' },
    PLATFORM_ANALYTICS: { icon: TrendingUp, color: 'text-purple-600', bgColor: 'bg-purple-50', displayName: 'Platform Analytics' },
    SUPER_ADMIN_BOOKING_RECEIVED: { icon: Bell, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Booking Received' },
    SUPER_ADMIN_DRIVER_ASSIGNED: { icon: User, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Driver Assigned' },
    SUPER_ADMIN_BOOKING_COMPLETED: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Booking Completed' },
    SUPER_ADMIN_DRIVER_ACCEPTED: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Driver Accepted' },
    SUPER_ADMIN_DRIVER_DECLINED: { icon: X, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Driver Declined' },
    SUPER_ADMIN_BOOKING_CANCELLED: { icon: X, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Booking Cancelled' },
    SUPER_ADMIN_HOSPITAL_REGISTERED: { icon: AlertCircle, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'Hospital Registered' },
    SUPER_ADMIN_FLEET_REGISTERED: { icon: Truck, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Fleet Registered' },
    
    // General notifications
    BOOKING_CONFIRMED: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Booking Confirmed' },
    PAYMENT_RECEIVED: { icon: DollarSign, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Payment Received' },
    MAINTENANCE_REMINDER: { icon: Calendar, color: 'text-yellow-600', bgColor: 'bg-yellow-50', displayName: 'Maintenance Reminder' },
  };

  // Fetch notifications based on user role
  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      let response;
      
      // Use different endpoints based on user role
      if (userRole === 'super_admin') {
        // Super Admin uses summary endpoint for overview
        response = await apiService.get('/notifications/super-admin/summary?params[timeRange]=24h');
        
        if (response.success) {
          const data = response.data || {};
          // Convert summary data to clickable notification-like format
          const summaryNotifications = [];
          
          // Add notifications by type (clickable summaries)
          if (data.notificationsByType && data.notificationsByType.length > 0) {
            data.notificationsByType.forEach(typeData => {
              summaryNotifications.push({
                notification_id: `type-${typeData.type}`,
                type: typeData.type,
                title: `${typeData.count} ${typeData.type.replace(/_/g, ' ')}`,
                message: `${typeData.count} notifications of this type`,
                created_at: new Date().toISOString(),
                read_at: null,
                priority: 'NORMAL',
                isSummary: true, // Flag to indicate this is a summary
                notificationType: typeData.type // Store the actual notification type
              });
            });
          }
          
          // Add total summary if there are notifications
          if (data.totalNotifications > 0) {
            summaryNotifications.unshift({
              notification_id: 'summary-total',
              type: 'PLATFORM_ANALYTICS',
              title: 'All Notifications',
              message: `${data.totalNotifications} total notifications in the last 24 hours`,
              created_at: new Date().toISOString(),
              read_at: null,
              priority: 'NORMAL',
              isSummary: true,
              notificationType: 'ALL'
            });
          }
          
          setNotifications(summaryNotifications);
          setUnreadCount(data.unreadNotifications || 0);
        }
      } else {
        // Other roles use the main notifications endpoint
        response = await apiService.get('/notifications');
        
        if (response.success) {
          const data = response.data || [];
          setNotifications(Array.isArray(data) ? data : data.notifications || []);
          setUnreadCount(data.filter ? data.filter(n => !n.read_at).length : 0);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await apiService.put(`/notifications/${notificationId}/read`);
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.notification_id === notificationId 
            ? { ...notif, read_at: new Date().toISOString() }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle notification click - either mark as read or navigate to details
  const handleNotificationClick = async (notification) => {
    if (notification.isSummary) {
      // For summary notifications, navigate to detailed notification page
      const notificationType = notification.notificationType;
      const url = notificationType === 'ALL' 
        ? '/super-admin/notifications' 
        : `/super-admin/notifications?type=${notificationType}`;
      
      // Navigate to notification page
      window.location.href = url;
    } else {
      // For individual notifications, mark as read
      await markAsRead(notification.notification_id);
    }
  };

  // Get priority configuration
  const getPriorityConfig = (priority) => {
    const priorityConfig = {
      critical: { color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
      high: { color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
      normal: { color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
      low: { color: 'text-gray-600', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' }
    };
    return priorityConfig[priority] || priorityConfig.normal;
  };

  // Format time
  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, userRole]);

  return (
    <>
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-1.5 sm:p-2 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-gray-100/70 transition-colors cursor-pointer"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
      </button>
    </div>

      {/* Notification Panel - Rendered via Portal */}
      {isOpen && createPortal(
        <div className="fixed top-16 right-8 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-[10000] max-h-96 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {userRole === 'fleet' ? 'Fleet' : userRole === 'hospital' ? 'Hospital' : userRole === 'super_admin' ? 'Super Admin' : 'System'} Notifications
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notifications</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, 10).map((notification) => {
                  const config = notificationTypes[notification.type] || notificationTypes.BOOKING_CONFIRMED;
                  const priorityConfig = getPriorityConfig(notification.priority);
                  const IconComponent = config.icon;
                  
                  return (
                    <div
                      key={notification.notification_id}
                      className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notification.read_at ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${config.bgColor}`}>
                          <IconComponent className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {notification.title}
                            </p>
                            <span className={`text-xs px-2 py-1 rounded-full ${priorityConfig.bgColor} ${priorityConfig.color}`}>
                              {notification.priority}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            {formatTime(notification.created_at)}
                          </p>
                        </div>
                        {!notification.read_at && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <button className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium">
                View all notifications
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export const DropdownArrowIcon = () => (
  <img src={ChevronDownIconSvg} alt="Dropdown" className="w-6 h-6 text-blue-950" />
);

// Live IST clock shown beside the notification bell — forced to
// Asia/Kolkata regardless of the viewer's own system timezone, since a
// dispatcher could be looking at this from anywhere but the label always
// reads IST, so the value has to actually be IST rather than trust the
// browser's local clock.
export const ISTClock = () => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = now.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
  const day = now.toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', weekday: 'short' });
  const date = now.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="flex items-center gap-3 sm:gap-4 select-none">
      {/* Date */}
      <div className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-zinc-800">
        <Calendar className="w-4 h-4 text-blue-600 shrink-0" />
        <span className="whitespace-nowrap">{day}, {date}</span>
      </div>

      <div className="h-5 w-px bg-[#1FCDFF40]/30 hidden sm:block" />

      {/* Time */}
      <div className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-zinc-800 tabular-nums">
        <Clock className="w-4 h-4 text-blue-600 shrink-0" />
        <span className="whitespace-nowrap">
          {time} <span className="text-[11px] font-bold text-zinc-500">IST</span>
        </span>
      </div>
    </div>
  );
};


// --- Universal Topbar Component ---
// This version is fully responsive and uses flex-wrap to adapt to different screen sizes.

export const UniversalTopBar = ({ 
  title, 
  subtitle, 
  onFilterChange, 
  onSearchChange,
  defaultFilter = 'Daily',
  userRole = 'fleet',
  onMenuToggle = null,
  isMobileMenuOpen = false,
  actionButton = null,
  hideUserDropdown = false
}) => {
  const [activeFilter, setActiveFilter] = useState(defaultFilter);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    try {
      console.log('🔓 Logging out user...');
      await logout();
      console.log('✅ Logout successful, redirecting to login...');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('❌ Logout failed:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  return (
    <div
      className="w-full flex items-center justify-between gap-2 sm:gap-4 p-2 sm:p-3 backdrop-blur-2xl bg-white/[0.02] rounded-lg border border-[#1FCDFF40]/15 shadow-2xl flex-wrap relative z-30"
      style={{ backdropFilter: 'blur(40px) saturate(180%)' }}
    >
      {/* Title & Subtitle Container (NO ICON in front of title as requested) */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {onMenuToggle && (
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-lg hover:bg-white/20 transition-all duration-300 flex-shrink-0"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center gap-1">
              <div className={`w-5 h-0.5 bg-zinc-800 transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></div>
              <div className={`w-5 h-0.5 bg-zinc-800 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></div>
              <div className={`w-5 h-0.5 bg-zinc-800 transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></div>
            </div>
          </button>
        )}

        <div className="flex flex-col justify-between flex-shrink-0">
          <h1 className="text-sm sm:text-base font-bold text-zinc-800 whitespace-nowrap">
            {title}
          </h1>
          {subtitle && (
            <p className="text-[10px] sm:text-xs font-semibold text-zinc-800/60 whitespace-nowrap hidden sm:block">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Right Side: Action Button, Date, Time, Divider, Notifications, Dropdown Arrow, Logout */}
      <div className="flex items-center gap-2.5 sm:gap-3.5 flex-shrink-0 ml-auto">
        {actionButton && (
          <>
            <div className="flex items-center shrink-0">
              {actionButton}
            </div>
            <div className="h-5 w-px bg-[#1FCDFF40]/30 hidden sm:block" />
          </>
        )}

        <ISTClock />

        <div className="h-5 w-px bg-[#1FCDFF40]/30 hidden sm:block" />

        {/* Notification Bell */}
        <NotificationDropdown userRole={userRole} />

        {/* User Options Dropdown (Hidden in Command Center) */}
        {userRole !== 'commandCenter' && !hideUserDropdown && (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="p-1.5 sm:p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label="Account menu"
              title="Account Options"
            >
              <ChevronDown className={`w-5 h-5 text-gray-700 transition-transform duration-200 ${showUserDropdown ? 'rotate-180 text-blue-600' : ''}`} />
            </button>

            {showUserDropdown && (
              <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-2xl border border-gray-100 p-2 z-50 text-left animate-in fade-in">
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 mb-1.5">
                  <p className="text-xs font-bold text-gray-900 truncate">
                    {user?.name || user?.email || 'User'}
                  </p>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200/60 mt-1">
                    {user?.role || 'USER'}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Logout Button */}
        <button 
          type="button"
          onClick={handleLogoutClick}
          className="p-1.5 sm:p-2 rounded-lg text-red-600 bg-red-50/80 hover:bg-red-100 hover:text-red-700 border border-red-200/60 shadow-2xs transition-all cursor-pointer flex items-center justify-center group"
          aria-label="Logout"
          title="Logout"
        >
          <Power className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-red-600 group-hover:text-red-700 group-hover:scale-105 transition-all stroke-[2.3]" />
        </button>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        title="Confirm Logout"
      >
        <div className="space-y-4">
          <p className="text-gray-700 text-base">
            Are you sure you want to logout?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={handleLogoutCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleLogoutConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200"
            >
              Yes, Logout
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};


// --- Main App Component (for Demonstration) ---
// This shows how to use the UniversalTopBar for different dashboards.

function App() {
  return (
    <div className="flex flex-col items-center w-full min-h-screen p-4 bg-gray-100 font-sans">
      <div className="w-full max-w-7xl mt-4">
        {/* The Universal Topbar in action */}
        <UniversalTopBar
          title="Patient Hub"
          subtitle="Check your reports and upcoming visits."
        />
      </div>
    </div>
  );
}

export default UniversalTopBar;

